import React from 'react';
import { cn } from '@/lib/utils';
import {
  BookOpen,
  Brain,
  Database,
  Globe,
  Lightbulb,
  Cpu,
  FileText,
  Search,
  MessageCircle,
} from 'lucide-react';
import type { Source, SourceType } from '@/types/trust';

/**
 * Visual mapping per source type — icon, label, neutral palette pair.
 * Colors intentionally restrained: the affordance should read as "provenance"
 * not as a colorful badge wall. Use application-UI design tokens, not brand
 * primary colors, so the chat surface stays calm.
 */
interface BadgeStyle {
  label: string;
  Icon: React.ElementType;
  fg: string;
  bg: string;
}

const BADGE_STYLES: Record<SourceType, BadgeStyle> = {
  'knowledge-article': { label: 'Article', Icon: BookOpen, fg: '#0F5132', bg: '#E7F3EC' },
  'knowledge-entry':   { label: 'Knowledge', Icon: FileText, fg: '#0F5132', bg: '#E7F3EC' },
  'memory':            { label: 'Memory', Icon: Brain, fg: '#1C3A64', bg: '#E3EAF5' },
  'data-record':       { label: 'Data', Icon: Database, fg: '#4B296B', bg: '#EDE6F4' },
  'device-state':      { label: 'Device', Icon: Cpu, fg: '#7A3B0E', bg: '#FBEEDD' },
  'web-url':           { label: 'Web', Icon: Globe, fg: '#334155', bg: '#E5E7EB' },
  'research-session':  { label: 'Research', Icon: Search, fg: '#0F5132', bg: '#E7F3EC' },
  'user-statement':    { label: 'You said', Icon: MessageCircle, fg: '#1C3A64', bg: '#E3EAF5' },
  'llm-training':      { label: 'General knowledge', Icon: Lightbulb, fg: '#6B7280', bg: '#F3F4F6' },
  'inference':         { label: 'Inferred', Icon: Lightbulb, fg: '#6B7280', bg: '#F3F4F6' },
};

interface SourceBadgeProps {
  source: Source;
  onClick?: (source: Source) => void;
  compact?: boolean;
}

export const SourceBadge: React.FC<SourceBadgeProps> = ({ source, onClick, compact = false }) => {
  const style = BADGE_STYLES[source.type] ?? BADGE_STYLES['inference'];
  const { Icon } = style;
  const labelText = source.label || style.label;
  const clickable = !!onClick;
  return (
    <button
      type="button"
      onClick={clickable ? () => onClick!(source) : undefined}
      disabled={!clickable}
      aria-label={`Source: ${style.label} — ${labelText}`}
      title={source.excerpt || labelText}
      className={cn(
        'inline-flex items-center gap-1 rounded-full border border-transparent',
        'text-[11px] font-medium whitespace-nowrap max-w-full overflow-hidden',
        clickable && 'cursor-pointer hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-400',
        !clickable && 'cursor-default',
        compact ? 'px-1.5 py-0.5' : 'px-2 py-0.5',
      )}
      style={{
        backgroundColor: style.bg,
        color: style.fg,
      }}
    >
      <Icon className="w-3 h-3 flex-shrink-0" aria-hidden="true" />
      <span className="truncate">
        {compact ? style.label : labelText}
      </span>
      {!compact && typeof source.trust === 'number' && (
        <span className="opacity-70" aria-hidden="true">
          · {Math.round(source.trust * 100)}%
        </span>
      )}
    </button>
  );
};

SourceBadge.displayName = 'SourceBadge';
